// src/pages/TasksPage/useTasksLogic.ts
import { useEffect, useMemo, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import type { DB, Id, Space, Task, NewTask, DeleteImageResp } from "./types";
import { useAuth } from "../../context/AuthContext";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import API_BASE from "../../services/api";
import {
  apiFetch,
  API_BASE_URL,
  getAuthToken,
  getCsrfToken,
} from "../../services/apiClient";

// Paleta igual que la UI de propiedades (consistencia con ManageColor)
const PALETTE = [
  "#EF4444",
  "#F97316",
  "#F59E0B",
  "#EAB308",
  "#84CC16",
  "#22C55E",
  "#10B981",
  "#14B8A6",
  "#06B6D4",
  "#0EA5E9",
  "#3B82F6",
  "#6366F1",
  "#8B5CF6",
  "#A855F7",
  "#D946EF",
  "#EC4899",
  "#F43F5E",
  "#64748B",
  "#6B7280",
  "#9CA3AF",
  "#111827",
  "#2B4C7E",
  "#47BEF5",
  "#6DD3FF",
];

const DEFAULT_COLUMNS_LIST = [
  { key: "titulo", label: "Título", visible: true, width: 260 },
  { key: "propiedad", label: "Propiedad", visible: true, width: 220 },
  { key: "prioridad", label: "Prioridad", visible: true, width: 130 },
  { key: "estatus", label: "Estatus", visible: true, width: 140 },
  { key: "registradoEl", label: "Registrado", visible: true, width: 140 },
  { key: "costo", label: "Costo", visible: true, width: 110 },
  { key: "imagenes", label: "Imágenes", visible: true, width: 120 },
];

const pickAutoColor = (i: number) => PALETTE[i % PALETTE.length];

async function fetchJson<T>(
  path: string,
  init?: RequestInit & { json?: any },
): Promise<T> {
  const body =
    init?.json !== undefined ? JSON.stringify(init.json) : (init?.body as BodyInit | undefined);
  const data = await apiFetch<T>(path, {
    ...init,
    body,
  });
  if (data == null) throw new Error(`Error en ${path}`);
  return data;
}

const api = {
  getDb: () => fetchJson<DB>("/tareas/db"),

  listTasks: (params: { spaceId: Id; categoryId?: Id | null }) => {
    const qs = new URLSearchParams();
    qs.set("spaceId", String(params.spaceId));
    if ("categoryId" in params)
      qs.set("categoryId", String(params.categoryId ?? ""));
    return fetchJson<Task[]>(`/tareas?${qs.toString()}`);
  },

  upsertTask: (task: Task | NewTask) =>
    fetchJson<Task>("/tareas", { method: "POST", json: task }),

  // No usamos `fetchJson` aquí porque el endpoint responde 204 No Content
  // (sin body) y `fetchJson` lanza si el resultado de `apiFetch` es null.
  // Como `apiFetch` además aliasa `null` entre "204 ok" y "error" (siempre
  // captura), no nos sirve para distinguir. Para el caso simple (single
  // delete) llamamos a apiFetch — los errores ya se notifican via toast.
  deleteTask: async (taskId: Id): Promise<void> => {
    await apiFetch<void>(`/tareas/${encodeURIComponent(String(taskId))}`, {
      method: "DELETE",
      errorAsModal: false,
    });
  },

  // Bulk delete: usa `fetch` nativo para poder distinguir success vs error
  // en cada request individual (apiFetch siempre devuelve null y se come
  // los errores via toast — sirve para single delete pero no para bulk
  // donde el caller necesita contar oks/fails). Replicamos la mecánica
  // crítica: Bearer token + cookie HttpOnly + header CSRF leído de la
  // cookie csrfToken. Lanza si !res.ok.
  deleteTaskRaw: async (taskId: Id): Promise<void> => {
    // CSRF: el backend lo exige en mutaciones cuando la auth viene por
    // cookie HttpOnly. Lo correcto es leerlo via `getCsrfToken()` que
    // primero mira la variable en memoria (set por /auth/login y /auth/me)
    // y solo cae a `document.cookie` si la cookie es legible — en prod
    // (front y back en hosts distintos) la cookie es cross-site y JS NO
    // puede leerla, por lo que el `document.cookie.split(...)` directo
    // que tenía antes siempre devolvía vacío y el backend respondía 403.
    const token = getAuthToken();
    const csrf = getCsrfToken();
    const headers: Record<string, string> = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await fetch(
      `${API_BASE_URL}/tareas/${encodeURIComponent(String(taskId))}`,
      { method: "DELETE", credentials: "include", headers },
    );
    if (!res.ok) {
      let msg = `Error ${res.status}`;
      try {
        const j = await res.json();
        msg = j?.message || j?.error?.message || msg;
      } catch {
        /* respuesta sin body */
      }
      throw new Error(msg);
    }
  },

  createSpace: async (payload: any) => {
    const { id, columns, mode, name, sectorIds } = payload;
    return fetchJson<Space>(`/tareas/spaces/${id}/settings`, {
      method: "PATCH",
      json: {
        columns,
        mode: mode ?? "board",
        name,
        // Solo enviamos sectorIds si vino (array, posiblemente vacío). Si
        // no vino, omitimos para no pisar el valor existente.
        ...(sectorIds !== undefined && { sectorIds }),
      },
    });
  },

  // Drawer de alertas (round 2026-05-06): tareas vencidas + recurrencias
  // próximas (≤ 7 días). Una sola llamada al backend para popular ambas
  // listas; backend ya filtra por rol.
  getAlertas: async (): Promise<{
    vencidas: Task[];
    recurrentesProximas: Task[];
  }> => {
    const json = await apiFetch<{
      vencidas: Task[];
      recurrentesProximas: Task[];
    }>(`/tareas/alertas`);
    if (!json) return { vencidas: [], recurrentesProximas: [] };
    return json;
  },

  // Eliminación total del espacio + todas sus tareas (en transacción backend).
  // El frontend exige doble confirmación antes de llamar acá. Devuelve
  // `deletedTasks` para que la UI muestre cuántas tareas se eliminaron.
  deleteSpace: async (
    spaceKey: Id,
  ): Promise<{ success: boolean; deletedTasks: number }> => {
    const json = await apiFetch<{ success: boolean; deletedTasks: number }>(
      `/tareas/spaces/${encodeURIComponent(String(spaceKey))}`,
      { method: "DELETE" },
    );
    if (!json) throw new Error("Error eliminando espacio");
    return json;
  },

  createCategory: (spaceId: Id, name: string, columns: any[]) =>
    fetchJson<{ id: Id; name: string }>(
      `/tareas/spaces/${encodeURIComponent(String(spaceId))}/categories`,
      { method: "POST", json: { name, columns } },
    ),

  // Renombrar categoria. Backend tambien acepta `order`, no lo usamos hoy.
  patchCategory: (spaceId: Id, categoryId: Id, name: string) =>
    fetchJson<{ id: Id; name: string }>(
      `/tareas/spaces/${encodeURIComponent(String(spaceId))}/categories/${encodeURIComponent(String(categoryId))}`,
      { method: "PATCH", json: { name } },
    ),

  // Eliminar categoria. Las tareas que la referenciaban quedan con
  // category_key=NULL (ver controller deleteCategory). Endpoint responde
  // 204 No Content, asi que usamos apiFetch directo (fetchJson lanza si
  // recibe null).
  deleteCategory: async (spaceId: Id, categoryId: Id): Promise<void> => {
    await apiFetch<void>(
      `/tareas/spaces/${encodeURIComponent(String(spaceId))}/categories/${encodeURIComponent(String(categoryId))}`,
      { method: "DELETE" },
    );
  },

  updatePropertyColor: (propertyId: Id, color: string | null) =>
    fetchJson<void>(
      `/tareas/properties/${encodeURIComponent(String(propertyId))}/color`,
      { method: "PATCH", json: { color } },
    ),

  updatePriorityColor: (priorityId: Id, color: string | null) =>
    fetchJson<void>(
      `/tareas/priorities/${encodeURIComponent(String(priorityId))}/color`,
      { method: "PATCH", json: { color } },
    ),

  batchUpdatePropertyColors: (items: { id: Id; color: string | null }[]) =>
    fetchJson<void>(`/tareas/properties/colors`, {
      method: "PATCH",
      json: { items },
    }),

  getDocuments: async (taskId: Id): Promise<any[]> => {
    const json = await apiFetch<{ data: any[] }>(
      `/tareas/${encodeURIComponent(String(taskId))}/documents`,
    );
    if (!json) throw new Error("Error al cargar documentos");
    return json.data || [];
  },

  uploadDocument: async (taskId: Id, file: File): Promise<any> => {
    const form = new FormData();
    form.append("file", file, file.name);
    const json = await apiFetch<{ data: any }>(
      `/tareas/${encodeURIComponent(String(taskId))}/documents`,
      { method: "POST", body: form },
    );
    if (!json) throw new Error("Error subiendo documento");
    return json.data;
  },

  deleteDocument: async (taskId: Id, key: string): Promise<any[]> => {
    const json = await apiFetch<{ data: any[] }>(
      `/tareas/${encodeURIComponent(String(taskId))}/documents?key=${encodeURIComponent(key)}`,
      { method: "DELETE" },
    );
    if (!json) throw new Error("Error eliminando documento");
    return json.data || [];
  },

  // Devuelve los items completos de la galeria (con `kind`, `mimeType`,
  // `originalName`, `url`, `thumbUrl`). El backend tambien expone `data`
  // como array plano de URLs por compat — lo ignoramos: necesitamos `kind`
  // para distinguir imagen vs video sin depender de la extension del URL
  // (las URLs firmadas son `/files/<JWT>` sin extension legible).
  getImages: async (taskId: Id): Promise<any[]> => {
    const json = await apiFetch<{ items?: any[]; data?: string[] }>(
      `/tareas/${encodeURIComponent(String(taskId))}/images`,
    );
    if (!json) throw new Error("Error al cargar imágenes");
    const { items, data } = json;
    if (Array.isArray(items) && items.length > 0) return items;
    // Fallback legacy: si el backend solo devolvio `data` (urls), envolvemos
    // como objetos para que el frontend siga funcionando (kind=image por defecto).
    return Array.isArray(data) ? data.map((url: string) => ({ url, kind: "image" })) : [];
  },

  // Devuelve el item completo (con `kind`) del archivo recien subido.
  uploadMediaFile: async (taskId: Id, file: File): Promise<any> => {
    const form = new FormData();
    form.append("file", file, file.name);
    const json = await apiFetch<{ item?: any; data?: string }>(
      `/tareas/${encodeURIComponent(String(taskId))}/images`,
      { method: "POST", body: form },
    );
    if (!json) throw new Error("Error subiendo archivo");
    if (json.item) return json.item;
    // Fallback legacy: si solo viene `data` (url string), envolvemos.
    return { url: json.data, kind: file.type.startsWith("video/") ? "video" : "image" };
  },

  deleteImage: async (taskId: Id, url: string): Promise<string[]> => {
    const json = await apiFetch<DeleteImageResp>(
      `/tareas/${encodeURIComponent(
        String(taskId),
      )}/images?url=${encodeURIComponent(url)}`,
      { method: "DELETE" },
    );
    if (!json || !json.success) {
      throw new Error(json?.error || json?.message || "Error eliminando imagen");
    }
    return json.data?.urls ?? [];
  },
};

export function useTasksLogic() {
  const { user } = useAuth();
  const [db, setDb] = useState<DB | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  // Espacio y categoría activos: la "pestaña" de /tareas. Antes se guardaban
  // a mano en sessionStorage con una clave global del navegador (dos admins en
  // el mismo equipo se pisaban la elección y duraba solo esa pestaña). Ahora
  // van por el hook compartido: por usuario y sobreviven a la recarga.
  // `refreshDb` descarta lo guardado si ese espacio/categoría ya no existe.
  const [activeSpaceId, setActiveSpaceId] = useFiltroPersistido<Id | null>(
    "tareas.espacio",
    null,
    { validar: (v) => v === null || (typeof v === "string" && v.length > 0) },
  );
  const [activeCategoryId, setActiveCategoryId] = useFiltroPersistido<Id | null>(
    "tareas.categoria",
    null,
    { validar: (v) => v === null || (typeof v === "string" && v.length > 0) },
  );

  const activeSpace: Space | null = useMemo(() => {
    if (!db || !activeSpaceId) return null;
    return db.spaces.find((s) => s.id === activeSpaceId) ?? null;
  }, [db, activeSpaceId]);

  const refreshDb = async () => {
    setLoading(true);
    try {
      const next = await api.getDb();
      const spaces = (next.spaces || []).map((s: any) => {
        return {
          ...s,
          columns:
            Array.isArray(s.columns) && s.columns.length > 0
              ? s.columns
              : DEFAULT_COLUMNS_LIST,
          categories: Array.isArray(s.categories) ? s.categories : [],
        };
      });
      let userData: any;
      if (user) {
        userData = await API_BASE.usersApi.getUser(user.id);
      }
      const rolesRaw = (userData as any)?.rol;
      const tipo = Array.isArray(rolesRaw)
        ? rolesRaw
        : rolesRaw
          ? [rolesRaw]
          : [];

      const sectorIdsRaw = (userData as any)?.sector ?? [];
      const sectorIds = Array.isArray(sectorIdsRaw)
        ? sectorIdsRaw
        : [sectorIdsRaw];

      // Preferir propiedadIds del user si existe, si no fallback a las propiedades del DB
      const userPropIdsRaw =
        (userData as any)?.propiedadIds ??
        (userData as any)?.propiedadesIds ??
        null;

      const propiedadIds = Array.isArray(userPropIdsRaw)
        ? userPropIdsRaw.map((x: any) => String(x))
        : next.propiedades
            .filter((p) => p.cliente === (userData as any).id)
            .map((p) => String(p.id));

      const mappedUser = userData
        ? {
            tipo: tipo.map((x: any) => String(x)),
            sectorIds: sectorIds.map((x: any) => String(x)),
            propiedadIds,
          }
        : null;

      setDb({ ...(next as any), spaces, user: mappedUser });
      const firstSpace = next.spaces[0] ?? null;
      // La elección guardada solo vale si ese espacio todavía existe: si se
      // borró, caemos al primero. Sin esto, un espacio eliminado dejaría la
      // página en el estado vacío para siempre (antes el problema moría al
      // cerrar la pestaña, porque se guardaba en sessionStorage).
      const spGuardado =
        activeSpaceId && next.spaces.some((s) => s.id === activeSpaceId)
          ? activeSpaceId
          : null;
      const spId = spGuardado ?? firstSpace?.id ?? null;
      setActiveSpaceId(spId);

      const sp = next.spaces.find((s) => s.id === spId) ?? firstSpace;

      // list => sin categorías
      if (sp?.mode === "list") {
        setActiveCategoryId(null);
      } else {
        const cats = sp?.categories ?? [];
        const firstCat = cats[0]?.id ?? null;
        // Misma regla para la categoría: se conserva solo si pertenece al
        // espacio que quedó activo.
        setActiveCategoryId((prev) =>
          prev && cats.some((c) => c.id === prev) ? prev : firstCat,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshTasks = async () => {
    if (!activeSpaceId || !activeSpace) {
      setTasks([]);
      return;
    }

    setLoading(true);
    try {
      const t =
        activeSpace.mode === "board"
          ? await api.listTasks({
              spaceId: activeSpaceId,
              categoryId: activeCategoryId,
            })
          : await api.listTasks({ spaceId: activeSpaceId });
      setTasks(t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpace, activeCategoryId]);

  const upsertTask = async (task: Task | NewTask) => {
    // Tareas nuevas se prependen al array (`[task, ...prev]` en vez de
    // `[...prev, task]`) para que queden ARRIBA — justo después de la
    // fila draft. Asi el usuario corrobora visualmente la fila recien
    // creada sin scrollear al final. Coincide con el order del backend
    // que es `createdAt DESC`. Round 2026-05-05.
    const hasId = "id" in task && (task as any).id != null;

    // `tempId` solo se setea para tareas NUEVAS (sin id). PERF/UX (round
    // 2026-06-15): antes la creación NO era optimista — esperaba la respuesta
    // del servidor antes de mostrar la fila, así que tras Enter no pasaba nada
    // visible durante toda la ida/vuelta de red. Ahora insertamos la fila al
    // instante con un id temporal y `_saving: true` (la UI muestra un spinner
    // "Guardando…"), y la reemplazamos por la real cuando el backend responde.
    let tempId: string | null = null;
    if (hasId) {
      setTasks((prev) => {
        const idx = prev.findIndex((t) => t.id === (task as Task).id);
        if (idx >= 0) {
          const copy = prev.slice();
          copy[idx] = task as Task;
          return copy;
        }
        return [task as Task, ...prev];
      });
    } else {
      tempId = `tmp_${
        crypto?.randomUUID?.() ?? Math.random().toString(16).slice(2)
      }`;
      const optimistic = { ...(task as any), id: tempId, _saving: true } as Task;
      setTasks((prev) => [optimistic, ...prev]);
    }

    // Toast con spinner SOLO en creación (tempId set). Es el "círculo de carga"
    // visible y global que indica que se está guardando; se cierra al confirmar
    // (éxito o error). `duration: 0` lo mantiene abierto hasta cerrarlo a mano.
    const hideLoading = tempId
      ? getMsg()?.loading("Guardando tarea…", 0)
      : undefined;

    try {
      const saved = await api.upsertTask(task);
      setTasks((prev) => {
        // Quita la fila optimista temporal (si la hubo) antes de insertar la real.
        const base = tempId ? prev.filter((t) => t.id !== tempId) : prev;
        const idx = base.findIndex((t) => t.id === saved.id);
        if (idx >= 0) {
          const copy = base.slice();
          copy[idx] = saved;
          return copy;
        }
        return [saved, ...base];
      });
      hideLoading?.();
      // `clientesNotificados` viene del backend cuando la tarea pasó a ser
      // visible para el cliente: ahí sale un correo con la observación. Se
      // avisa SIEMPRE (no solo al crear) porque marcar "ver cliente" en una
      // tarea existente también dispara el correo, y el admin tiene que
      // enterarse de que salió — no se puede deshacer. Round 2026-07-27.
      const avisados = Number((saved as any)?.clientesNotificados) || 0;
      // Round 2026-07-28: si el dueño apagó los avisos, la tarea se comparte
      // igual pero nadie recibe correo. Callarlo dejaría al admin creyendo que
      // el cliente ya se enteró.
      const correosApagados = Boolean((saved as any)?.correosDesactivados);
      if (avisados) {
        getMsg()?.success(
          `Observación compartida — se está enviando el aviso por correo a ${avisados} cliente(s).`,
        );
      } else if (correosApagados) {
        getMsg()?.info(
          "Observación compartida. No se envió correo: los avisos están desactivados en Configuración → Correos al cliente.",
        );
      } else if (tempId) {
        getMsg()?.success("Tarea creada");
      }
    } catch (e) {
      console.error(e);
      hideLoading?.();
      getMsg()?.error(
        tempId ? "Error al crear la tarea" : "Error al actualizar la tarea",
      );
      // En creación: quita la fila optimista (nunca llegó a existir en backend).
      // En edición: refresca para re-sincronizar el valor real.
      if (tempId) {
        const tid = tempId;
        setTasks((prev) => prev.filter((t) => t.id !== tid));
      } else {
        refreshTasks();
      }
    }
  };

  const deleteTask = async (taskId: Id) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    try {
      await api.deleteTask(taskId);
    } catch (e) {
      console.error(e);
      getMsg()?.error("Error al eliminar la tarea");
      refreshTasks();
    }
  };

  // Borrado en lote — admin/superadmin only (lo gobierna la UI). Va en
  // paralelo despachando al endpoint single `DELETE /api/tareas/:id` (no hay
  // endpoint bulk en backend; cada borrado es transacción independiente).
  // Mismo patrón que useInventarioLogic.onBulkDelete.
  // Optimismo: removemos las filas localmente; si hay fallos refrescamos para
  // re-sincronizar (las que sí se borraron quedan removidas en backend).
  const bulkDeleteTasks = async (
    ids: Id[],
  ): Promise<{ ok: number; fail: number }> => {
    if (!ids.length) return { ok: 0, fail: 0 };
    const idSet = new Set(ids.map((x) => String(x)));
    setTasks((prev) => prev.filter((t) => !idSet.has(String(t.id))));
    const results = await Promise.all(
      ids.map((id) =>
        api.deleteTaskRaw(id).then(
          () => true,
          (err) => {
            console.error("bulkDeleteTasks", id, err);
            return false;
          },
        ),
      ),
    );
    const ok = results.filter(Boolean).length;
    const fail = results.length - ok;
    if (fail > 0) await refreshTasks();
    return { ok, fail };
  };

  // Mover tareas en bloque a otra categoria del mismo space. Reusa el
  // endpoint upsert (POST /tareas con id existente) — el controller hace
  // un Sequelize update con los campos del body. Mismo patron que
  // bulkDeleteTasks: parallel + cuenta ok/fail. Optimismo: removemos
  // localmente las que se mueven a otra categoria (solo si hay
  // activeCategoryId, porque en modo "list" el listado no filtra por
  // categoria), y refresh solo si hay fails.
  const bulkMoveTasks = async (
    ids: Id[],
    targetCategoryId: Id,
  ): Promise<{ ok: number; fail: number }> => {
    if (!ids.length) return { ok: 0, fail: 0 };
    const idSet = new Set(ids.map((x) => String(x)));
    const tasksToMove = tasks.filter((t) => idSet.has(String(t.id)));

    if (activeCategoryId && activeCategoryId !== targetCategoryId) {
      setTasks((prev) => prev.filter((t) => !idSet.has(String(t.id))));
    }

    const results = await Promise.all(
      tasksToMove.map((t) =>
        api
          .upsertTask({ ...(t as any), categoryId: targetCategoryId })
          .then(
            () => true,
            (err) => {
              console.error("bulkMoveTasks", t.id, err);
              return false;
            },
          ),
      ),
    );
    const ok = results.filter(Boolean).length;
    const fail = results.length - ok;
    if (fail > 0) await refreshTasks();
    return { ok, fail };
  };

  // `createSpace` se reusa para CREAR y RENOMBRAR (la UI hace
  // store.createSpace({ id: s.id, name: trimmed }) en el rename inline).
  // Antes siempre reseteaba activeSpaceId/activeCategoryId a la primera
  // categoría — al renombrar te mandaba a la primera cat aunque estuvieras
  // en otra, dando la falsa impresión de "tabla vacía" después del rename.
  // Ahora detectamos rename mirando si el `id` ya existe en `db.spaces` y
  // en ese caso solo refrescamos `db` (mantiene activeSpaceId/CategoryId
  // intactos). Round 2026-05-05.
  const createSpace = async (payload: {
    columns?: any[];
    id?: string;
    name?: string;
    mode?: "board" | "list";
    sectorIds?: number[];
  }) => {
    setLoading(true);
    try {
      const isRename = !!(
        payload.id && db?.spaces.some((s) => s.id === payload.id)
      );
      const sp = await api.createSpace(payload);
      await refreshDb();
      if (!isRename) {
        setActiveSpaceId(sp.id);
        setActiveCategoryId(
          sp.mode === "board" ? (sp.categories?.[0]?.id ?? null) : null,
        );
      }
      // Defensa contra race del useEffect: refreshTasks explícito para
      // garantizar que la tabla se re-renderee con datos frescos. En un
      // rename no cambia el space_key ni el category_key, así que las
      // tareas son las mismas — pero el closure del useEffect podría no
      // haberse re-ejecutado si la referencia de activeSpace se mantuvo.
      await refreshTasks();
    } finally {
      setLoading(false);
    }
  };

  // Eliminación de espacio (custom). La UI hace dos confirmaciones (1ª:
  // ¿eliminar el espacio? 2ª: vas a borrar también las N tareas dentro). Si
  // borrabas el space activo, caemos al primer space disponible para que la
  // UI no quede en un estado inválido (activeSpaceId apuntando a algo que
  // ya no existe).
  const deleteSpace = async (spaceKey: Id): Promise<number> => {
    const result = await api.deleteSpace(spaceKey);
    if (activeSpaceId === spaceKey) {
      setActiveSpaceId(null);
      setActiveCategoryId(null);
    }
    await refreshDb();
    return result.deletedTasks;
  };

  const createCategory = async (spaceId: Id, name: string, columns: any[]) => {
    setLoading(true);
    try {
      const cat = await api.createCategory(spaceId, name, columns);
      await refreshDb();
      setActiveCategoryId(cat.id);
    } finally {
      setLoading(false);
    }
  };

  // Renombrar categoria. Refrescamos `db` para que el sidebar reciba el
  // nuevo nombre. La categoria activa se preserva — solo cambia su label.
  // refreshTasks explicito porque el useEffect que reacciona a cambios en
  // `activeSpace` podía no dispararse de forma confiable tras el rename
  // (referencia de objeto cambia, pero el batch de React puede colapsar
  // las updates y el efecto quedar saltado).
  const renameCategory = async (spaceId: Id, categoryId: Id, name: string) => {
    await api.patchCategory(spaceId, categoryId, name);
    await refreshDb();
    await refreshTasks();
  };

  // Eliminar categoria. Las tareas que la referenciaban quedan con
  // category_key=NULL en backend (no se borran). Si la categoria activa era
  // la borrada, caemos a la primera disponible para no dejar la UI en un
  // estado invalido. refreshTasks explicito por el mismo motivo que
  // renameCategory.
  const removeCategory = async (spaceId: Id, categoryId: Id) => {
    await api.deleteCategory(spaceId, categoryId);
    if (activeCategoryId === categoryId) {
      const sp = db?.spaces.find((s) => s.id === spaceId);
      const remaining = (sp?.categories ?? []).filter((c) => c.id !== categoryId);
      setActiveCategoryId(remaining[0]?.id ?? null);
    }
    await refreshDb();
    await refreshTasks();
  };

  const updatePropertyColor = async (propertyId: Id, color: string | null) => {
    setDb((prev) =>
      prev
        ? {
            ...prev,
            propiedades: prev.propiedades.map((p) =>
              p.id === propertyId ? { ...p, color } : p,
            ),
          }
        : prev,
    );

    try {
      await api.updatePropertyColor(propertyId, color);
    } catch (e) {
      console.error(e);
      getMsg()?.error("Error al actualizar el color");
      refreshDb();
    }
  };

  const autoAssignPropertyColors = async () => {
    if (!db) return;

    const mapped = db.propiedades.map((p, i) => ({
      ...p,
      color: p.color ?? pickAutoColor(i),
    }));

    setDb({ ...db, propiedades: mapped });

    try {
      await api.batchUpdatePropertyColors(
        mapped.map((p) => ({ id: p.id, color: p.color ?? null })),
      );
    } catch (e) {
      console.error(e);
      getMsg()?.error("Error al asignar colores");
      refreshDb();
    }
  };

  return {
    db,
    user,
    tasks,
    loading,
    activeSpace,
    activeSpaceId,
    setActiveSpaceId,

    activeCategoryId,
    setActiveCategoryId,

    refreshDb,
    refreshTasks,

    upsertTask,
    deleteTask,
    bulkDeleteTasks,
    bulkMoveTasks,

    createSpace,
    deleteSpace,
    createCategory,
    renameCategory,
    removeCategory,

    updatePropertyColor,
    autoAssignPropertyColors,

    getImages: api.getImages,
    uploadMediaFile: api.uploadMediaFile,
    deleteImage: api.deleteImage,
    getAlertas: api.getAlertas,

    getDocuments: api.getDocuments,
    uploadDocument: api.uploadDocument,
    deleteDocument: api.deleteDocument,
  };
}

